import { useState, useEffect } from 'react';
import { Search, Plus, User, AlertCircle, X } from 'lucide-react';
import type { PatientWithDoctors, CreatePatientData } from '@/services/api/patients.service';
import { patientsService } from '@/services/api/patients.service';
import { useContext } from '@/hooks/useContext';
import { toast } from 'react-hot-toast';

interface PatientSearchModalProps {
  onSelect: (patient: PatientWithDoctors) => void;
  onClose: () => void;
  currentDoctorId?: string | null;
  currentDoctorName?: string;
}

type ModalStep = 'search-cpf' | 'search-list' | 'link-confirm' | 'create-form';

export function PatientSearchModal({ 
  onSelect, 
  onClose, 
  currentDoctorId, 
  currentDoctorName 
}: PatientSearchModalProps) {
  const { doctor, clinic } = useContext();
  const doctorId = currentDoctorId || doctor?.id;
  const doctorName = currentDoctorName || doctor?.name;

  const [step, setStep] = useState<ModalStep>('search-cpf');
  const [cpf, setCpf] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<PatientWithDoctors[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<PatientWithDoctors[]>([]);
  const [existingPatient, setExistingPatient] = useState<PatientWithDoctors | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form state for new patient
  const [formData, setFormData] = useState<CreatePatientData>({
    fullName: '',
    organizationId: clinic?.organizationId || '',
    cpf: '',
    email: '',
    birthDate: '',
    isInsurance: false,
  });

  useEffect(() => {
    if (step === 'search-list') {
      loadDoctorPatients();
    }
  }, [step, doctorId]);

  useEffect(() => {
    if (searchTerm.trim() && step === 'search-list') {
      const filtered = patients.filter((patient) =>
        patient.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.cpf?.includes(searchTerm)
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(patients);
    }
  }, [searchTerm, patients, step]);

  const loadDoctorPatients = async () => {
    if (!doctorId) return;
    
    try {
      setLoading(true);
      const response = await patientsService.getAll({ limit: 1000 });
      // Filter patients by current doctor
      const doctorPatients = response.data.filter(p => 
        p.doctorIds && p.doctorIds.includes(doctorId)
      );
      setPatients(doctorPatients);
      setFilteredPatients(doctorPatients);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao carregar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleCpfSearch = async () => {
    if (!cpf || cpf.length < 11) {
      toast.error('CPF inválido');
      return;
    }

    try {
      setLoading(true);
      // Search for patient by CPF
      const response = await patientsService.getAll({ limit: 1000 });
      const patientWithCpf = response.data.find(p => p.cpf === cpf);

      if (patientWithCpf) {
        // Check if patient is already linked to this doctor
        if (patientWithCpf.doctorIds && patientWithCpf.doctorIds.includes(doctorId!)) {
          // Already linked, select directly
          onSelect(patientWithCpf);
          onClose();
        } else {
          // Patient exists but not linked to this doctor
          setExistingPatient(patientWithCpf);
          setStep('link-confirm');
        }
      } else {
        // Patient doesn't exist, go to create form
        setFormData(prev => ({ ...prev, cpf }));
        setStep('create-form');
      }
    } catch (error: any) {
      toast.error('Erro ao buscar paciente');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkExistingPatient = async () => {
    if (!existingPatient || !doctorId) return;

    try {
      setLoading(true);
      await patientsService.addToDoctor(existingPatient.id, doctorId);
      toast.success('Paciente vinculado ao médico com sucesso');
      
      // Reload patient with updated doctor IDs
      const response = await patientsService.getAll({ limit: 1000 });
      const updatedPatient = response.data.find(p => p.id === existingPatient.id);
      
      if (updatedPatient) {
        onSelect(updatedPatient);
      }
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao vincular paciente');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePatient = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!doctorId) {
      toast.error('Médico não selecionado');
      return;
    }

    try {
      setCreating(true);
      // Create patient
      const newPatient = await patientsService.create(formData);
      
      // Link to doctor
      await patientsService.addToDoctor(newPatient.id, doctorId);
      
      toast.success('Paciente cadastrado com sucesso');
      
      // Reload to get updated patient with doctor IDs
      const response = await patientsService.getAll({ limit: 1000 });
      const createdPatient = response.data.find(p => p.id === newPatient.id);
      
      if (createdPatient) {
        onSelect(createdPatient);
      }
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao cadastrar paciente');
    } finally {
      setCreating(false);
    }
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Render CPF Search Step
  if (step === 'search-cpf') {
    return (
      <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Buscar Paciente</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Digite o CPF do paciente para buscar ou cadastrar
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF *
                </label>
                <input
                  type="text"
                  value={formatCPF(cpf)}
                  onChange={(e) => setCpf(e.target.value.replace(/\D/g, ''))}
                  placeholder="000.000.000-00"
                  maxLength={14}
                  className="w-full border rounded-lg px-3 py-2"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCpfSearch}
                  disabled={loading || cpf.length < 11}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Buscando...' : 'Buscar'}
                </button>
                <button
                  onClick={() => setStep('search-list')}
                  className="flex-1 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50"
                >
                  Ver Lista
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Link Confirmation Step
  if (step === 'link-confirm' && existingPatient) {
    return (
      <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold">Paciente Encontrado</h2>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="font-medium">{existingPatient.fullName}</p>
              <p className="text-sm text-gray-600">CPF: {formatCPF(existingPatient.cpf)}</p>
              {existingPatient.email && (
                <p className="text-sm text-gray-600">Email: {existingPatient.email}</p>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Este paciente já está cadastrado mas não está vinculado ao médico{' '}
              <strong>{doctorName}</strong>. Deseja vincular?
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('search-cpf')}
                className="flex-1 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50"
              >
                Voltar
              </button>
              <button
                onClick={handleLinkExistingPatient}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Vinculando...' : 'Vincular e Selecionar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Create Form Step
  if (step === 'create-form') {
    return (
      <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Cadastrar Novo Paciente</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreatePatient} className="space-y-4">
              {/* Doctor (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Médico
                </label>
                <input
                  type="text"
                  value={doctorName || 'Médico'}
                  readOnly
                  className="w-full border rounded-lg px-3 py-2 bg-gray-50 cursor-not-allowed"
                />
              </div>

              {/* CPF (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF *
                </label>
                <input
                  type="text"
                  value={formatCPF(formData.cpf || '')}
                  readOnly
                  className="w-full border rounded-lg px-3 py-2 bg-gray-50 cursor-not-allowed"
                />
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              {/* Birth Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Nascimento
                </label>
                <input
                  type="date"
                  value={formData.birthDate || ''}
                  onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              {/* Insurance */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isInsurance}
                    onChange={(e) => setFormData({ ...formData, isInsurance: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Possui convênio</span>
                </label>
              </div>

              {formData.isInsurance && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Convênio
                  </label>
                  <input
                    type="text"
                    value={formData.insuranceProvider || ''}
                    onChange={(e) => setFormData({ ...formData, insuranceProvider: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep('search-cpf')}
                  className="flex-1 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? 'Cadastrando...' : 'Cadastrar e Selecionar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Render Search List Step
  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-xl font-bold">Pacientes de {doctorName}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Input */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome, email ou CPF..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={() => setStep('search-cpf')}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Buscar por CPF / Novo Paciente
          </button>
        </div>

        {/* Patient List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="text-center py-8">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'Nenhum paciente encontrado' : 'Nenhum paciente vinculado a este médico'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPatients.map((patient) => (
                <button
                  key={patient.id}
                  onClick={() => {
                    onSelect(patient);
                    onClose();
                  }}
                  className="w-full text-left p-4 border rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{patient.fullName}</h3>
                      <div className="mt-1 space-y-1">
                        {patient.cpf && (
                          <p className="text-sm text-gray-600">CPF: {formatCPF(patient.cpf)}</p>
                        )}
                        {patient.email && (
                          <p className="text-sm text-gray-600">Email: {patient.email}</p>
                        )}
                        {patient.birthDate && (
                          <p className="text-sm text-gray-600">
                            Nascimento: {formatDate(patient.birthDate)}
                          </p>
                        )}
                      </div>
                    </div>
                    {patient.isInsurance && (
                      <span className="ml-3 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                        Convênio
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
